<template>
  <div class="page-container">

    <!-- Header: título + navegación de mes -->
    <div class="page-title-container d-flex align-items-center justify-content-between flex-wrap">
      <h1 class="page-title mb-0">Calendario de vacaciones</h1>
      <div class="month-nav d-flex align-items-center mt-2 mt-md-0">
        <button type="button" class="month-btn" @click="prevMonth">
          <i class="ri-arrow-left-s-line"></i>
        </button>
        <span class="month-label mx-3">{{ monthLabel }}</span>
        <button type="button" class="month-btn" @click="nextMonth">
          <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="d-flex flex-wrap align-items-center mb-3 calendar-filters">
      <div class="filter-field mr-3 mb-2">
        <label class="label-filter mb-1">Empresa</label>
        <SearchableDropdown
          :key="'company-' + filterKey"
          :options="companies"
          :oldValue="filters.companyId"
          placeholder="Todas las empresas"
          @selected="onCompanySelected"
        />
      </div>
      <div class="filter-field mr-3 mb-2">
        <label class="label-filter mb-1">Área</label>
        <SearchableDropdown
          :key="'area-' + filterKey"
          :options="areas"
          :oldValue="filters.areaId"
          placeholder="Todas las áreas"
          @selected="onAreaSelected"
        />
      </div>
      <div class="mb-2 mt-md-3">
        <button type="button" class="button button-light button-sm" @click="clearFilters">
          <i class="ri-refresh-line mr-1"></i>Limpiar
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && calendarRows.length === 0" class="text-center text-muted py-5">
      <i class="ri-calendar-2-line" style="font-size: 3rem; opacity: 0.35;"></i>
      <p class="mt-3 h5">No hay vacaciones programadas para este mes</p>
      <p class="small">Intenta con otro mes o revisa los filtros aplicados.</p>
    </div>

    <!-- Grilla Gantt -->
    <div v-else-if="calendarRows.length > 0" class="gantt-outer">
      <div class="gantt-wrapper">

        <!-- Fila header: nombre + días -->
        <div class="gantt-row gantt-header" :style="gridStyle">
          <div class="gantt-col-name gantt-header-name">Colaborador</div>
          <div
            v-for="d in dayNumbers"
            :key="'h-' + d"
            class="gantt-header-day"
            :class="{ weekend: isWeekend(d), today: isToday(d) }"
          >{{ d }}</div>
        </div>

        <!-- Filas de empleados -->
        <div
          v-for="(row, rowIdx) in calendarRows"
          :key="rowIdx"
          class="gantt-row gantt-data-row"
          :style="gridStyle"
        >
          <!-- Nombre empleado (sticky) -->
          <div class="gantt-col-name" :title="row.employeeName">
            {{ row.employeeName }}
          </div>

          <!-- Celdas de días (fondo) -->
          <div
            v-for="d in dayNumbers"
            :key="'d-' + d"
            class="gantt-day-cell"
            :class="{ weekend: isWeekend(d), today: isToday(d) }"
          ></div>

          <!-- Barras de vacaciones -->
          <div
            v-for="(bar, barIdx) in row.bars"
            :key="'bar-' + barIdx"
            class="gantt-bar"
            :class="bar.status === 'APROBADA' ? 'gantt-bar-approved' : 'gantt-bar-pending'"
            :style="barStyle(bar)"
            :title="bar.tooltip"
            @click="$router.push({ name: 'vacation-requests-detail', params: { id: bar.requestId } })"
          >
            <span v-if="bar.endDay - bar.startDay >= 2" class="gantt-bar-label">
              {{ bar.endDay - bar.startDay + 1 }}d
            </span>
          </div>
        </div>

      </div>

      <!-- Leyenda -->
      <div class="gantt-legend d-flex align-items-center mt-3">
        <div class="legend-item mr-4">
          <span class="legend-dot legend-dot-approved"></span>
          <span class="small ml-1">APROBADA</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot legend-dot-pending"></span>
          <span class="small ml-1">PENDIENTE</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { Alert, SearchableDropdown } from 'wize-admin'
import { VacationService, CompanyService, AreaService } from '@/core/services'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'VacationCalendar',
  components: { SearchableDropdown },

  data() {
    const now = new Date()
    return {
      currentYear:  now.getFullYear(),
      currentMonth: now.getMonth(),
      requests:  [],
      companies: [],
      areas:     [],
      loading:    false,
      filterKey:  0,
      filters: {
        companyId: null,
        areaId:    null,
      },
    }
  },

  computed: {
    monthLabel() {
      return `${MONTH_NAMES[this.currentMonth]} ${this.currentYear}`
    },
    daysInMonth() {
      return new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
    },
    dayNumbers() {
      return Array.from({ length: this.daysInMonth }, (_, i) => i + 1)
    },
    gridStyle() {
      return `grid-template-columns: 280px repeat(${this.daysInMonth}, minmax(30px, 1fr))`
    },
    calendarRows() {
      const year  = this.currentYear
      const month = this.currentMonth
      const firstDay = new Date(year, month, 1)
      const lastDay  = new Date(year, month + 1, 0)

      // Solo APROBADA y PENDIENTE con intersección en el mes
      const relevant = this.requests.filter(r => {
        if (r.status !== 'APROBADA' && r.status !== 'PENDIENTE') return false
        const start = new Date(r.startDate)
        const end   = new Date(r.endDate)
        return start <= lastDay && end >= firstDay
      })

      // Agrupar por empleado
      const map = {}
      relevant.forEach(r => {
        const key = r.employeeName || r.employeeId
        if (!map[key]) map[key] = { employeeName: r.employeeName, bars: [] }

        const start   = new Date(r.startDate)
        const end     = new Date(r.endDate)
        const startDay = start < firstDay ? 1 : start.getDate()
        const endDay   = end > lastDay ? lastDay.getDate() : end.getDate()

        map[key].bars.push({
          requestId: r.id,
          startDay,
          endDay,
          status:  r.status,
          tooltip: `${r.startDate}  →  ${r.endDate}  (${r.calendarDays} días)`,
        })
      })

      return Object.values(map).sort((a, b) =>
        (a.employeeName || '').localeCompare(b.employeeName || '')
      )
    },
  },

  methods: {
    async loadData() {
      try {
        this.loading = true
        this.$store.dispatch('app/loading', true)
        const params = {
          year:  this.currentYear,
          month: this.currentMonth + 1,
        }
        if (this.filters.companyId) params.companyId = this.filters.companyId
        if (this.filters.areaId)    params.areaId    = this.filters.areaId
        const resp = await VacationService.getRequests(params)
        this.requests = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el calendario')
      } finally {
        this.loading = false
        this.$store.dispatch('app/loading', false)
      }
    },

    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
      this.loadData()
    },

    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
      this.loadData()
    },

    isWeekend(day) {
      const dow = new Date(this.currentYear, this.currentMonth, day).getDay()
      return dow === 0 || dow === 6
    },

    isToday(day) {
      const now = new Date()
      return (
        now.getFullYear() === this.currentYear &&
        now.getMonth()    === this.currentMonth &&
        now.getDate()     === day
      )
    },

    barStyle(bar) {
      return `grid-column: ${bar.startDay + 1} / ${bar.endDay + 2}; grid-row: 1;`
    },

    onCompanySelected(id) {
      this.filters.companyId = id
      this.loadData()
    },

    onAreaSelected(id) {
      this.filters.areaId = id
      this.loadData()
    },

    clearFilters() {
      this.filters.companyId = null
      this.filters.areaId    = null
      this.filterKey++
      this.loadData()
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({
        key:   c.id,
        label: c.businessName || c.name,
      }))
      this.areas = (areasResp.payload || []).map(a => ({
        key:   a.id,
        label: a.description,
      }))
    } catch (e) {
      console.error('Error al cargar catálogos del calendario', e)
    }
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">

/* ── Filtros ─────────────────────────────────────── */
.calendar-filters
  .filter-field
    min-width 220px

/* ── Contenedor con scroll horizontal ───────────── */
.gantt-outer
  width 100%

.gantt-wrapper
  overflow-x auto
  border 1px solid #dee2e6
  border-radius 6px

/* ── Fila base ───────────────────────────────────── */
.gantt-row
  display grid
  position relative
  border-bottom 1px solid #e9ecef

  &:last-child
    border-bottom none

/* ── Columna nombre empleado ─────────────────────── */
.gantt-col-name
  position sticky
  left 0
  background #fff
  z-index 3
  padding 0 10px
  display flex
  align-items center
  border-right 2px solid #dee2e6
  font-size 13px
  min-height 44px
  white-space nowrap
  overflow hidden
  text-overflow ellipsis
  max-width 280px

/* ── Fila header ─────────────────────────────────── */
.gantt-header
  background #f8f9fa
  border-bottom 2px solid #dee2e6

.gantt-header-name
  background #f8f9fa
  font-weight 700
  font-size 12px
  text-transform uppercase
  color #6c757d

.gantt-header-day
  display flex
  align-items center
  justify-content center
  font-size 11px
  font-weight 600
  color #6c757d
  border-right 1px solid #e9ecef
  min-height 36px
  background #f8f9fa

  &.weekend
    background #efefef
    color #adb5bd

  &.today
    background #e3f2fd
    color #1976d2
    font-weight 700

/* ── Celda de día (filas de datos) ───────────────── */
.gantt-day-cell
  border-right 1px solid #f0f0f0
  min-height 44px

  &.weekend
    background #fafafa

  &.today
    background #e8f4fd

/* ── Barras de vacaciones ────────────────────────── */
.gantt-bar
  align-self center
  height 24px
  border-radius 4px
  cursor pointer
  z-index 4
  display flex
  align-items center
  overflow hidden
  transition opacity 0.15s, box-shadow 0.15s

  &:hover
    opacity 0.85
    box-shadow 0 2px 8px rgba(0,0,0,0.18)

.gantt-bar-approved
  background #28a745

.gantt-bar-pending
  background repeating-linear-gradient(
    45deg,
    #ffc107 0px,
    #ffc107 6px,
    #fff9e6 6px,
    #fff9e6 12px
  )
  border 1px solid #e0a800

.gantt-bar-label
  font-size 10px
  font-weight 700
  color rgba(255,255,255,0.9)
  padding-left 5px
  white-space nowrap
  pointer-events none

.gantt-bar-pending .gantt-bar-label
  color #856404

/* ── Navegación de mes ───────────────────────────── */
.month-nav
  .month-btn
    width 32px
    height 32px
    border 1px solid #dee2e6
    border-radius 4px
    background #fff
    cursor pointer
    display flex
    align-items center
    justify-content center
    font-size 18px
    color #495057
    padding 0
    transition background 0.1s

    &:hover
      background #f8f9fa

  .month-label
    font-size 16px
    font-weight 600
    min-width 160px
    text-align center
    color #212529

/* ── Leyenda ─────────────────────────────────────── */
.gantt-legend
  .legend-item
    display flex
    align-items center

  .legend-dot
    width 16px
    height 16px
    border-radius 3px
    display inline-block

  .legend-dot-approved
    background #28a745

  .legend-dot-pending
    background repeating-linear-gradient(
      45deg,
      #ffc107 0px,
      #ffc107 6px,
      #fff9e6 6px,
      #fff9e6 12px
    )
    border 1px solid #e0a800
</style>
